/* ============================================================================
 * MTE - Diagnostic runtime (Track J.4 - 2026-09-14)
 * ----------------------------------------------------------------------------
 * Source-of-truth (canonique) :
 *   - Boussole J.2 section 8 Diagnostic - algorithme runtime 12-16 (selection deterministe)
 *   - Lumen J.1 section 1 + 4 - mapa 42 competences canoniques + 9 items nouveaux
 *
 * Scope J.4 (4 pieces) :
 *   1. selectActivities(profileId, pool, sessionHistory) - algo Boussole section 8
 *   2. saveResult(profileId, competence, S|C|F|N)        - constellation D-NX-006
 *   3. getConstellation(profileId)                       - read S/C/F/N par competence
 *   4. ConstellationResultDomain (CE1=3 / CM1=5)          - agregation par matiere
 *
 * Storage key :
 *   - mte:diagnostic:v1:<kidId>                          (kidId-namespaced per Profile System #0)
 *
 * Non-bloqueant per D-NX-010 - Plus tard toujours disponible (gere UI).
 * Pas d'IA lecture/prononciation (D-NX-007 + D-NX-008).
 *
 * Conformance :
 *   - deterministe par profileId (meme input -> meme sequence reproductible)
 *   - pas de Math.random() runtime
 *   - FR/MA balance approx 50/50 (+/- 1)
 *   - difficulte 1/2/3 equilibree
 *   - anti-repetition : exclut competences S/C/F/N identiques aux 4 dernieres sessions
 * ========================================================================== */

import type { BanqueItem, Archetype } from './banque-types';

/**
 * TargetLevel = current grade child is entering (used to map to previous-level bank).
 * PreviousLevel = the bank actually loaded (assesses acquis du N-1).
 *
 * Pattern pedagogique universelle :
 *   target = CE1  →  diagnostique les acquis de fin de CP
 *   target = CE2  →  diagnostique les acquis de fin de CE1
 *   target = CM1  →  diagnostique les acquis de fin de CE2
 *   target = CM2  →  diagnostique les acquis de fin de CM1
 *
 * NOTE : CM2 n'existe PAS dans 'PreviousLevel' — la banque CM1-fim est utilisée
 * pour diagnostic fin CM1 (= entrée CM2). Banque CM2-fim sera créée Phase 2.
 */
export type NiveauDiagnostic = 'CP' | 'CE1' | 'CE2' | 'CM1' | 'CM2';
export type TargetLevel = 'CE1' | 'CE2' | 'CM1' | 'CM2';
export type PreviousLevel = 'CP' | 'CE1' | 'CE2' | 'CM1';
export type DomaineDiagnostic = 'FR' | 'MA';
export type Constellation = 'S' | 'C' | 'F' | 'N';

export const TARGET_TO_PREVIOUS_BANK: Record<TargetLevel, PreviousLevel> = {
  CE1: 'CP',
  CE2: 'CE1',
  CM1: 'CE2',
  CM2: 'CM1',
};

export function targetToPreviousLevel(target: TargetLevel): PreviousLevel {
  return TARGET_TO_PREVIOUS_BANK[target];
}

export function resolveBankForTarget(target: TargetLevel, banks: Partial<Record<PreviousLevel, ReadonlyArray<BanqueItem>>>): ReadonlyArray<BanqueItem> {
  const prev = TARGET_TO_PREVIOUS_BANK[target];
  return banks[prev] ?? [];
}

export interface SessionHistoryEntry {
  date: string;                 // ISO timestamp
  competenceResults: Record<string, Constellation>;
}

export interface DiagnosticProfile {
  id: string;                   // kidId
  firstName?: string;
  schoolLevel?: NiveauDiagnostic;
  heroId?: string | null;
  /** Map competence -> constellation (4 dernieres sessions merge) */
  constellation: Record<string, Constellation>;
  /** Historique des 4 dernieres sessions (FIFO) */
  sessionHistory: SessionHistoryEntry[];
  /** Aggregate par matiere (CE1=3 / CM1=5) */
  profilesByDomain: Record<string, Constellation>;
}

const STORAGE_PREFIX = 'mte:diagnostic:v1';

export function getDiagnosticStorageKey(profileId: string): string {
  if (!profileId || typeof profileId !== 'string') {
    throw new Error('getDiagnosticStorageKey requires non-empty profileId');
  }
  return `${STORAGE_PREFIX}:${profileId}`;
}

/* ---------------------------------------------------------------------------
 * Persistance S/C/F/N constellation par competence
 * ------------------------------------------------------------------------- */

export function loadConstellation(profileId: string): DiagnosticProfile {
  const key = getDiagnosticStorageKey(profileId);
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return emptyConstellation(profileId);
    }
    const raw = window.localStorage.getItem(key);
    if (!raw) return emptyConstellation(profileId);
    const parsed = JSON.parse(raw) as Partial<DiagnosticProfile>;
    return {
      id: profileId,
      firstName: parsed.firstName,
      schoolLevel: parsed.schoolLevel,
      heroId: parsed.heroId ?? null,
      constellation: parsed.constellation ?? {},
      sessionHistory: Array.isArray(parsed.sessionHistory) ? parsed.sessionHistory.slice(-4) : [],
      profilesByDomain: parsed.profilesByDomain ?? {},
    };
  } catch {
    return emptyConstellation(profileId);
  }
}

export function saveConstellation(profile: DiagnosticProfile): void {
  const key = getDiagnosticStorageKey(profile.id);
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(profile));
  } catch {
    // storage full / disabled - silent, non-bloqueant
  }
}

export function resetConstellation(profileId: string): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.removeItem(getDiagnosticStorageKey(profileId));
}

function emptyConstellation(profileId: string): DiagnosticProfile {
  return {
    id: profileId,
    constellation: {},
    sessionHistory: [],
    profilesByDomain: {},
  };
}

/* ---------------------------------------------------------------------------
 * saveResult - enregistre le resultat d'une competence (S/C/F/N)
 * Met a jour la constellation + append l'historique + recompute l'agregation
 * ------------------------------------------------------------------------- */

export function saveResult(
  profileId: string,
  competence: string,
  result: Constellation,
  _archetype: Archetype | string,
): DiagnosticProfile {
  const current = loadConstellation(profileId);
  const next: DiagnosticProfile = {
    ...current,
    constellation: { ...current.constellation, [competence]: result },
    sessionHistory: [
      ...current.sessionHistory,
      { date: new Date().toISOString(), competenceResults: { [competence]: result } },
    ],
  };
  // Keep last 4 sessions only (FIFO)
  if (next.sessionHistory.length > 4) {
    next.sessionHistory = next.sessionHistory.slice(-4);
  }
  next.profilesByDomain = aggregateProfilesByDomain(next);
  saveConstellation(next);
  return next;
}

function aggregateProfilesByDomain(profile: DiagnosticProfile): Record<string, Constellation> {
  const agg: Record<string, Constellation> = {};
  const counts: Record<string, { S: number; C: number; F: number; N: number }> = {};
  for (const [comp, val] of Object.entries(profile.constellation)) {
    const isMA = comp.startsWith('MA-');
    const key = isMA ? 'maths' : 'francais';
    if (!counts[key]) counts[key] = { S: 0, C: 0, F: 0, N: 0 };
    counts[key][val] += 1;
  }
  for (const [k, c] of Object.entries(counts)) {
    if (c.S > 0) agg[k] = 'S';
    else if (c.C > 0) agg[k] = 'C';
    else if (c.F > 0) agg[k] = 'F';
    else agg[k] = 'N';
  }
  return agg;
}

/* ---------------------------------------------------------------------------
 * getConstellation - read access (alias court pour Boussole section 8)
 * ------------------------------------------------------------------------- */

export function getConstellation(profileId: string): Record<string, Constellation> {
  return loadConstellation(profileId).constellation;
}

/* ---------------------------------------------------------------------------
 * selectActivities - algorithme Boussole section 8 (12-16 activites deterministes)
 * ------------------------------------------------------------------------- */

export interface SelectActivitiesInput {
  profileId: string;
  /** previous-level (the bank actually loaded) — equals TARGET_TO_PREVIOUS_BANK[targetLevel] */
  schoolLevel: NiveauDiagnostic;
  /** target-level is the grade child is entering; runtime uses TARGET_TO_PREVIOUS_BANK mapping */
  targetLevel?: TargetLevel;
  /** all 4 banks available; runtime picks the correct one via targetLevel */
  banks?: Partial<Record<NiveauDiagnostic, ReadonlyArray<BanqueItem>>>;
  /** explicit pool (legacy / single-bank mode) */
  pool?: ReadonlyArray<BanqueItem>;
  sessionHistory?: ReadonlyArray<SessionHistoryEntry>;
}

export interface SelectActivitiesResult {
  activities: BanqueItem[];
  /** Stats pour debug / Atlas audit */
  stats: {
    cible: number;
    selected: number;
    frCount: number;
    maCount: number;
    difficulty1: number;
    difficulty2: number;
    difficulty3: number;
    archetypeCoverage: Record<string, number>;
    excludedByConstellation: number;
  };
}

const TARGET_COUNT: Record<NiveauDiagnostic, number> = {
  CP: 24,
  CE1: 24,
  CE2: 24,
  CM1: 24,
  CM2: 24, // same as CM1 — uses same bank per TARGET_TO_PREVIOUS_BANK
};
const MIN_ITEMS = 23;
const MAX_ITEMS = 26;

export function selectActivities(input: SelectActivitiesInput): SelectActivitiesResult {
  const { profileId, schoolLevel } = input;
  // Resolve pool: prefer banks[schoolLevel] if provided, else fallback to legacy pool
  let pool: ReadonlyArray<BanqueItem>;
  if (input.banks && input.banks[schoolLevel] && input.banks[schoolLevel]!.length > 0) {
    pool = input.banks[schoolLevel]!;
  } else if (input.pool) {
    pool = input.pool;
  } else if (input.banks) {
    pool = resolveBankForTarget(
      input.targetLevel ?? (schoolLevel === 'CP' ? 'CE1' : schoolLevel === 'CE1' ? 'CE2' : schoolLevel === 'CE2' ? 'CM1' : 'CM2'),
      input.banks,
    );
  } else {
    pool = [];
  }
  const target = TARGET_COUNT[schoolLevel];

  if (!profileId) throw new Error('selectActivities requires profileId');
  if (!pool.length) {
    return emptyResult(target);
  }

  // M-C2 Workstream A -- pool warning if bank smaller than floor
  if (pool.length < MIN_ITEMS) {
    if (typeof console !== 'undefined') {
      console.warn(
        '[diagnostic-runtime] bank pool for ' + schoolLevel + ' has ' + pool.length + ' items < MIN_ITEMS=' + MIN_ITEMS + '; result will return whatever is available.',
      );
    }
  }

  const filtered = pool.filter((it) => it.schoolLevel === schoolLevel).filter((it) => isItemRenderable(it));
  if (typeof console !== 'undefined' && filtered.length < pool.filter((it) => it.schoolLevel === schoolLevel).length) {
    const dropped = pool.filter((it) => it.schoolLevel === schoolLevel).length - filtered.length;
    console.warn('[diagnostic-runtime] dropped ' + dropped + ' malformed items for ' + schoolLevel);
  }
  // M-C2.4 smoke fix 3: max 1 evaluation_1_minute per session (avoid repetition)
  // Categorise by text usage if meta.texte_usage exists
  const evalItems = filtered.filter((it) => (it).meta && (it).meta.texte_usage === 'evaluation_1_minute');
  let filteredNonEval = filtered.filter((it) => !((it).meta && (it).meta.texte_usage === 'evaluation_1_minute'));
  // Reserve at most 1 evaluation_1_minute if available
  let reservedEval: BanqueItem | null = null;
  if (evalItems.length > 0) {
    const evalSeed = hashSeed(profileId + ':eval');
    const sortedEval = deterministicShuffle([...evalItems], evalSeed);
    const picked = sortedEval[0];
    if (picked) reservedEval = picked;
    filteredNonEval = [...filteredNonEval, ...evalItems.filter((it) => picked && it.id !== picked.id)];
  }
  const seed = hashSeed(profileId);
  const excludeCompetences = computeExclusionSet(input.sessionHistory);

  const availableNonEval = filteredNonEval.filter((it) => !excludeCompetences.has(it.competence));
  let availablePool = [...availableNonEval];
  if (reservedEval && !excludeCompetences.has(reservedEval.competence)) {
    availablePool.push(reservedEval);
  }
  const sorted = deterministicShuffle(availablePool, seed);

  // Baseline pick: cible items, 50/50 FR/MA interleaved.
  const halfFR = Math.ceil(target / 2);
  const halfMA = target - halfFR;

  const frPool = sorted.filter((it) => !isMaths(it));
  const maPool = sorted.filter((it) => isMaths(it));

  const frSelected = pickBalanced(frPool, halfFR, schoolLevel, seed);
  const maSelected = pickBalanced(maPool, halfMA, schoolLevel, seed + 1);

  // Adaptatif: detect competences with only one evidence and ambiguous signal (C/F),
  // add +1 more item from that domain (cap MAX_ITEMS=26). Skip if already 'S'.
  let pickedFr = [...frSelected];
  let pickedMa = [...maSelected];
  const lastSession = (input.sessionHistory && input.sessionHistory.length > 0)
    ? input.sessionHistory[input.sessionHistory.length - 1]
    : null;
  const lastResults = lastSession ? lastSession.competenceResults : {};
  const ambiguousByCompetence: Record<string, number> = {};
  for (const comp of Object.keys(lastResults)) {
    const val = lastResults[comp];
    if (val === 'S') continue; // skip if already S
    if (val === 'C' || val === 'F') {
      ambiguousByCompetence[comp] = (ambiguousByCompetence[comp] || 0) + 1;
    }
  }

  // Domains covered by baseline (FR/MA)
  const usedIds = new Set([...pickedFr, ...pickedMa].map((it) => it.id));
  const remainingFr = frPool.filter((it) => !usedIds.has(it.id));
  const remainingMa = maPool.filter((it) => !usedIds.has(it.id));

  // For each ambiguous competence, try to add +1 more item (iteratively) up to MAX_ITEMS.
  let total = pickedFr.length + pickedMa.length;
  const ambiguousKeys = Object.keys(ambiguousByCompetence);
  for (const comp of ambiguousKeys) {
    if (total >= MAX_ITEMS) break;
    const isMathComp = comp.startsWith('MA-');
    const source = isMathComp ? remainingMa : remainingFr;
    const candidate = source.shift();
    if (!candidate) continue;
    if (isMathComp) pickedMa.push(candidate);
    else pickedFr.push(candidate);
    total++;
  }

  // Floor enforcement: never return <MIN_ITEMS unless bank pool is too small
  // (handled implicitly: total >= MIN_ITEMS unless pool < MIN_ITEMS; warning already logged).

  const merged = interleave(pickedFr, pickedMa).slice(0, MAX_ITEMS);
  const stats = computeStats(merged, target, excludeCompetences.size);

  return { activities: merged, stats };
}

/* --- internal helpers --- */

function emptyResult(target: number): SelectActivitiesResult {
  return {
    activities: [],
    stats: {
      cible: target,
      selected: 0,
      frCount: 0,
      maCount: 0,
      difficulty1: 0,
      difficulty2: 0,
      difficulty3: 0,
      archetypeCoverage: {},
      excludedByConstellation: 0,
    },
  };
}

function isMaths(it: BanqueItem): boolean {
  return it.competence.startsWith('MA-');
}

function hashSeed(profileId: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < profileId.length; i++) {
    h = Math.imul(h ^ profileId.charCodeAt(i), 16777619);
  }
  return h >>> 0;
}

function deterministicShuffle<T>(arr: T[], seed: number): T[] {
  let s = seed >>> 0;
  const rand = () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const a = out[i] as T;
    const b = out[j] as T;
    out[i] = b;
    out[j] = a;
  }
  return out;
}

function computeExclusionSet(history?: ReadonlyArray<SessionHistoryEntry>): Set<string> {
  if (!history || history.length === 0) return new Set();
  const last4 = history.slice(-4);
  const seen: Record<string, Constellation[]> = {};
  for (const s of last4) {
    for (const [comp, val] of Object.entries(s.competenceResults)) {
      if (!seen[comp]) seen[comp] = [];
      seen[comp]!.push(val);
    }
  }
  const excluded = new Set<string>();
  for (const [comp, vals] of Object.entries(seen)) {
    if (vals.length >= 4 && vals.every((v) => v === vals[0])) {
      excluded.add(comp);
    }
  }
  return excluded;
}

function pickBalanced(
  arr: BanqueItem[],
  n: number,
  schoolLevel: NiveauDiagnostic,
  seed: number,
): BanqueItem[] {
  if (arr.length <= n) return arr;
  const sorted = deterministicShuffle(arr, seed);

  const target: Record<NiveauDiagnostic, { d1: number; d2: number; d3: number }> = {
  CP: { d1: 4, d2: 7, d3: 5 },
  CE1: { d1: 4, d2: 7, d3: 5 },
  CE2: { d1: 4, d2: 7, d3: 5 },
  CM1: { d1: 5, d2: 7, d3: 5 },
  CM2: { d1: 5, d2: 7, d3: 5 },
};
  const t = target[schoolLevel];

  const d1 = sorted.filter((it) => difficultyOf(it) === 1);
  const d2 = sorted.filter((it) => difficultyOf(it) === 2);
  const d3 = sorted.filter((it) => difficultyOf(it) === 3);

  const out: BanqueItem[] = [];
  out.push(...d1.slice(0, t.d1));
  out.push(...d2.slice(0, t.d2));
  out.push(...d3.slice(0, t.d3));

  if (out.length < n) {
    const used = new Set(out.map((it) => it.id));
    for (const it of sorted) {
      if (out.length >= n) break;
      if (!used.has(it.id)) out.push(it);
    }
  }
  return out.slice(0, n);
}

function difficultyOf(it: BanqueItem): 1 | 2 | 3 {
  switch (it.archetype) {
    case 'qcm':
    case 'vf':
    case 'saisie':
      return 1;
    case 'texte-a-trou':
    case 'association':
    case 'manipulation-glisser':
    case 'lecture-voix-haute':
      return 2;
    case 'ordre':
    case 'production-ecrite':
    case 'dictee':
      return 3;
    default:
      return 2;
  }
}

function interleave<T>(a: T[], b: T[]): T[] {
  const out: T[] = [];
  const max = Math.max(a.length, b.length);
  for (let i = 0; i < max; i++) {
    if (i < a.length) out.push(a[i] as T);
    if (i < b.length) out.push(b[i] as T);
  }
  return out;
}


function isItemRenderable(item: BanqueItem): boolean {
  if (!item || !item.id || typeof item.id !== 'string') return false;
  if (!item.competence || typeof item.competence !== 'string') return false;
  if (!item.enonce || typeof item.enonce !== 'string') return false;
  if (!item.archetype || typeof item.archetype !== 'string') return false;
  if (!item.schoolLevel) return false;
  const it = item as unknown as Record<string, unknown>;
  switch (item.archetype) {
    case 'qcm':
      return Array.isArray(it.options) && (it.options as unknown[]).length > 0 && typeof it.bonneReponse === 'string';
    case 'vf':
      return typeof it.bonneReponse === 'boolean';
    case 'saisie':
      return typeof it.bonneReponse === 'string' && (it.bonneReponse as string).length > 0;
    case 'texte-a-trou':
      return Array.isArray(it.enonceSegments) && (it.enonceSegments as unknown[]).length > 0 && Array.isArray(it.trous) && (it.trous as unknown[]).length > 0;
    case 'ordre':
      return Array.isArray(it.itemsOrdre) && (it.itemsOrdre as unknown[]).length > 0 && Array.isArray(it.ordreCorrect) && (it.ordreCorrect as unknown[]).length > 0;
    case 'association':
      return Array.isArray(it.pairesGauche) && (it.pairesGauche as unknown[]).length > 0 && Array.isArray(it.pairesDroite) && (it.pairesDroite as unknown[]).length > 0 && typeof it.pairesCorrectes === 'object' && it.pairesCorrectes !== null;
    case 'manipulation-glisser':
      return typeof it.spec === 'object' && it.spec !== null;
    case 'production-ecrite':
      return typeof it.minWords === 'number' && (it.minWords as number) > 0;
    case 'dictee':
      return typeof it.audioUrl === 'string' && (it.audioUrl as string).length > 0 && typeof it.bonneReponse === 'string' && (it.bonneReponse as string).length > 0;
    case 'lecture-voix-haute':
      return typeof it.textALire === 'string' && (it.textALire as string).length > 0 && typeof it.dureeMaxSecondes === 'number' && (it.dureeMaxSecondes as number) > 0;
    default:
      return false;
  }
}

function computeStats(
  selected: BanqueItem[],
  cible: number,
  excluded: number,
): SelectActivitiesResult['stats'] {
  const frCount = selected.filter((it) => !isMaths(it)).length;
  const maCount = selected.length - frCount;
  const d1 = selected.filter((it) => difficultyOf(it) === 1).length;
  const d2 = selected.filter((it) => difficultyOf(it) === 2).length;
  const d3 = selected.filter((it) => difficultyOf(it) === 3).length;
  const archetypeCoverage: Record<string, number> = {};
  for (const it of selected) {
    archetypeCoverage[it.archetype] = (archetypeCoverage[it.archetype] ?? 0) + 1;
  }
  return {
    cible,
    selected: selected.length,
    frCount,
    maCount,
    difficulty1: d1,
    difficulty2: d2,
    difficulty3: d3,
    archetypeCoverage,
    excludedByConstellation: excluded,
  };
}


/* ---------------------------------------------------------------------------
 * LectureVoixHaute — runtime selecter (Ana-direct 19/09 ~13:00)
 * 3 categories : lecture_debutante | entrainement_fluence | evaluation_1_minute
 * ------------------------------------------------------------------------- */
export type FluenceTexteUsage = 'lecture_debutante' | 'entrainement_fluence' | 'evaluation_1_minute';
export type FluencePreparation = 'avec' | 'sans';
export type MomentAnnee = 'T1' | 'T2' | 'T3';

export interface FluenceProfileContext {
  /** First session or child still learning to read */
  isFirstSession?: boolean;
  /** Average mots_corrects/min over last 3 sessions */
  recentScoreMotsPerMin?: number;
  /** Current trimester (T1 = start, T2 = middle, T3 = end of year) */
  momentAnnee: MomentAnnee;
  /** Parent/runtime asks for formal evaluation (default false) */
  demandeEvaluationFormelle?: boolean;
}

/**
 * Extended LectureVoixHaute item with fluence-specific meta.
 * BanqueItem is a discriminated union — we define this as a separate
 * interface (not extending BanqueItem) with explicit fields.
 */
export interface BanqueItemLectureVoixHauteExtended {
  id: string;
  archetype: 'lecture-voix-haute';
  competence: string;
  schoolLevel: 'CP' | 'CE1' | 'CE2' | 'CM1';
  enonce: string;
  consigne?: string;
  evalMode: 'HUMAN';
  textALire: string;
  dureeMaxSecondes: number;
  meta?: {
    usage?: FluenceTexteUsage;
    preparation?: FluencePreparation;
    motsTotal?: number;
    sourceOfficielle?: string;
    [key: string]: unknown;
  };
}

/**
 * selecterItemFluenceRuntime — choose appropriate FluenceTexteUsage given context.
 * NEVER use lecture_debutante or entrainement_fluence as sole basis for formal 1-min
 * measurement when child already reads faster (Ana explicite).
 */
export function selecterItemFluenceRuntime(
  bank: ReadonlyArray<BanqueItemLectureVoixHauteExtended>,
  ctx: FluenceProfileContext,
): BanqueItemLectureVoixHauteExtended {
  const usage = resolveFluenceUsage(bank, ctx);
  const candidates = bank.filter((it) => it.meta?.usage === usage);
  const fallback = bank.filter((it) => it.meta?.usage === 'entrainement_fluence');
  const pool = candidates.length > 0 ? candidates : fallback.length > 0 ? fallback : bank;
  if (pool.length === 0) {
    throw new Error('selecterItemFluenceRuntime: empty pool');
  }
  // Deterministic shuffle using profileId-like seed (simplified)
  const idx = Math.floor(((ctx.recentScoreMotsPerMin ?? 0) + ctx.momentAnnee.length + (ctx.isFirstSession ? 1 : 0)) % pool.length);
  return pool[idx] ?? pool[0]!;
}

function resolveFluenceUsage(
  _bank: ReadonlyArray<BanqueItemLectureVoixHauteExtended>,
  ctx: FluenceProfileContext,
): FluenceTexteUsage {
  if (ctx.demandeEvaluationFormelle) return 'evaluation_1_minute';
  if (ctx.isFirstSession) return 'lecture_debutante';
  const score = ctx.recentScoreMotsPerMin ?? 0;
  const isMidYear = ctx.momentAnnee === 'T2' || ctx.momentAnnee === 'T3';
  if (score >= 30 && isMidYear) return 'entrainement_fluence';
  if (score < 30) return 'lecture_debutante';
  return 'entrainement_fluence';
}

/* ---------------------------------------------------------------------------
 * 2-types audio — instruction_audio vs stimulus_audio
 * (Documentation only — runtime enforcement is per item rendering logic.)
 *
 * instruction_audio = apoio à leitura da consigne (Ana 22:50) :
 *   gs : 'propose' (botão destacado, proposto por defeito)
 *   cp : 'bouton'  (opt-in via "Écouter")
 *   ce1 : 'bouton'
 *   ce2 : 'bouton'
 *   cm1 : 'absent' (autonomia leitura)
 *   cm2 : 'absent'
 *   JAMAIS autoplay au chargement
 *
 * stimulus_audio = áudio pedagógico (constitui a atividade) :
 *   Puede existir em TODOS níveis quando ouvir é a competência avaliada
 *   (ex: anglais, comprehension orale, discrimination sons)
 *   Toujours requis pour evaluation formelle
 * ------------------------------------------------------------------------- */
export type InstructionAudioMode = 'propose' | 'bouton' | 'absent';

/* ---------------------------------------------------------------------------
 * FluenceHistory mock/dev (localStorage) + production (D1 future)
 * Per Ana-direct 19/09 §8 — localStorage apenas mock/dev; production = D1 lié kidId.
 * ------------------------------------------------------------------------- */
export const FLUENCE_STORAGE_PREFIX = 'mte:diagnostic:fluence';

export function getFluenceStorageKey(kidId: string): string {
  if (!kidId || typeof kidId !== 'string') {
    throw new Error('getFluenceStorageKey requires non-empty kidId');
  }
  return `${FLUENCE_STORAGE_PREFIX}:${kidId}`;
}

export interface FluenceHistoryEntry {
  sessionDate: string;        // ISO datetime
  texteId: string;
  niveau: PreviousLevel;
  motsLus: number;
  erreurs: number;
  motsCorrectsPerMin: number;  // computed
  tauxErreurPct: number;      // computed
  niveauProgression: 'training_cp' | 'training_ce1' | 'reference' | 'training_sup';
}

export function loadFluenceHistory(kidId: string): FluenceHistoryEntry[] {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const raw = window.localStorage.getItem(getFluenceStorageKey(kidId));
    if (!raw) return [];
    return JSON.parse(raw) as FluenceHistoryEntry[];
  } catch {
    return [];
  }
}

export function saveFluenceHistory(kidId: string, entries: FluenceHistoryEntry[]): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(getFluenceStorageKey(kidId), JSON.stringify(entries));
  } catch {
    // silent, non-bloqueant
  }
}

export function appendFluenceHistory(kidId: string, entry: FluenceHistoryEntry): FluenceHistoryEntry[] {
  const current = loadFluenceHistory(kidId);
  const next = [...current, entry];
  saveFluenceHistory(kidId, next);
  return next;
}

export function computeMteFluenceStats(motsLus: number, erreurs: number): { motsCorrectsPerMin: number; tauxErreurPct: number } {
  const motsCorrects = Math.max(0, motsLus - erreurs);
  const tauxErreurPct = motsLus > 0 ? (erreurs / motsLus) * 100 : 0;
  return { motsCorrectsPerMin: motsCorrects, tauxErreurPct };
}
